import {
  ChangeDetectionStrategy,
  Component,
  inject,
  DestroyRef,
  signal,
  Input
} from '@angular/core';
import { AsyncPipe, I18nPluralPipe } from '@angular/common';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { DialogRef, DIALOG_DATA } from '@angular/cdk/dialog';
import { filter, map, switchMap } from 'rxjs';

import { Notifier } from '@core/notifications';
import { countdown, addLeadingZero, indicateLoading } from '@core/helpers';
import { hours, minutes, seconds } from '@core/common/plurals';
import { AuthState, User, UserAvatarSize } from '@app/modules/users';
import { UserAvatarPipe } from '@app/modules/users/pipes';

import { DailyRewardsState } from '../../daily-rewards.state';
import { Avatar, CheckedUser } from '../../daily-rewards.entity';
import { AvatarUrlPipe } from '../../pipes/avatar-url.pipe';

type DialogData = {
  modalDesc: string;
  avatars: Avatar[];
  loggedUser: User;
};

const AVATAR_DETECTED_MESSAGE = $localize`:@@bonuses.daily_rewards.avatar_found:Аватар обнаружен`;

@Component({
    selector: 'dialog-avatar-bonus',
    templateUrl: './avatar-bonus.dialog.html',
    styleUrl: './avatar-bonus.dialog.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        AsyncPipe,
        I18nPluralPipe,
        UserAvatarPipe,
        AvatarUrlPipe
    ]
})
export class AvatarBonusDialog {
  public readonly state = inject(DailyRewardsState);
  public readonly data = inject<DialogData>(DIALOG_DATA);
  public readonly dialogRef = inject(DialogRef);

  private readonly notifier = inject(Notifier);
  private readonly destroyRef = inject(DestroyRef);
  private readonly authState = inject(AuthState);

  public readonly isCheckingAvatar = signal<boolean>(false);
  public readonly hoursPlurals = hours;
  public readonly minutesPlurals = minutes;
  public readonly secondsPlurals = seconds;
  public readonly UserAvatarSize = UserAvatarSize;

  @Input()
  public modalDesc?: string;

  public readonly countdown$ = toObservable(this.state.avatarCheckedUser)
    .pipe(
      filter((checkedUser): checkedUser is CheckedUser => !!checkedUser),
      switchMap((checkedUser) => {
        const timeShift = this.authState.timeDiff();
        const endAtMs = Date.now() + checkedUser.timeToPass * 1000;

        return countdown(endAtMs, { timeShift });
      }),
      map((cd) => {
        const hoursLeft = cd.duration!.hours();
        const minutesLeft = cd.duration!.minutes();
        const secondsLeft = cd.duration!.seconds();

        return {
          hours: [addLeadingZero(hoursLeft, false), hoursLeft],
          minutes: [addLeadingZero(minutesLeft, false), minutesLeft],
          seconds: [addLeadingZero(secondsLeft, false), secondsLeft]
        };
      })
    );

  selectAvatar(id: number): void {
    this.state.setSelectedAvatarId(id);
  }

  checkAvatar(): void {
    this.state.checkAvatar()
      .pipe(
        indicateLoading(this.isCheckingAvatar),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.notifier.success(AVATAR_DETECTED_MESSAGE);
      });
  }
}
